import { Timeline, Text, Badge, Group, Stack } from '@mantine/core'
import {
  IconCircleCheck,
  IconUserPlus,
  IconUserMinus,
  IconEdit,
} from '@tabler/icons-react'
import { ResponsiveTitle } from '../ResponsiveTitle/ResponsiveTitle'
import { formatDate } from '../../lib/utils'
import type { Changelog } from '../../lib/types'
import styles from './ChangelogTimeline.module.css'

interface ChangelogTimelineProps {
  changelog: Changelog[]
}

export function ChangelogTimeline({ changelog }: ChangelogTimelineProps) {
  const getChangeIcon = (fieldName: string) => {
    if (fieldName === 'rsvp_status') return IconCircleCheck
    if (fieldName === 'additional_guest' && !changelog) return IconUserPlus
    if (fieldName === 'additional_guest') return IconUserMinus
    if (fieldName === 'dietary_restrictions' || fieldName === 'comments') return IconEdit
    return IconEdit
  }

  const getChangeColor = (fieldName: string, changedBy: string) => {
    if (fieldName === 'rsvp_status') return 'green'
    if (fieldName === 'additional_guest') return 'blue'
    return changedBy === 'admin' ? 'grape' : 'teal'
  }

  if (changelog.length === 0) {
    return (
      <Stack gap="md">
        <ResponsiveTitle order={3} className={styles.title}>
          Änderungsverlauf
        </ResponsiveTitle>
        <Text c="dimmed" ta="center" py="md">
          Noch keine Änderungen
        </Text>
      </Stack>
    )
  }

  return (
    <Stack gap="md">
      <ResponsiveTitle order={3} className={styles.title}>
        Änderungsverlauf
      </ResponsiveTitle>

      <Timeline active={changelog.length} bulletSize={24} lineWidth={2} className={styles.timeline}>
        {changelog.map((entry) => {
          const Icon = getChangeIcon(entry.field_name)
          const color = getChangeColor(entry.field_name, entry.changed_by)

          return (
            <Timeline.Item
              key={entry.id}
              bullet={<Icon size={14} />}
              title={
                <Group gap="xs">
                  <Text size="sm" fw={500}>
                    {entry.description}
                  </Text>
                  <Badge size="sm" variant="light" color={color}>
                    {entry.changed_by === 'admin' ? 'Admin' : 'Gast'}
                  </Badge>
                </Group>
              }
              color={color}
            >
              <Text size="xs" c="dimmed">
                {formatDate(new Date(entry.timestamp), 'en')}
              </Text>

              {entry.old_value && entry.new_value && (
                <Group gap="xs" mt="xs">
                  <Badge size="sm" variant="outline" color="gray">
                    {entry.old_value}
                  </Badge>
                  <Text size="xs" c="dimmed">
                    →
                  </Text>
                  <Badge size="sm" variant="outline" color={color}>
                    {entry.new_value}
                  </Badge>
                </Group>
              )}
            </Timeline.Item>
          )
        })}
      </Timeline>
    </Stack>
  )
}
